import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Pencil } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { AuthImage } from "@/components/AuthImage";
import { SidesPicker } from "@/components/SidesPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money, priceForItem, DELIVERY_OPTIONS } from "@/lib/pricing";
import { getTheme } from "@/lib/theme";

export default function Cart() {
  const { itemsFor, remove, updateQty, updateSides, clear } = useCart();
  const { selectedBusinessId, user } = useAuth();
  const nav = useNavigate();
  const theme = getTheme(selectedBusinessId);

  const bizItems = useMemo(() => itemsFor(selectedBusinessId), [itemsFor, selectedBusinessId]);

  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [fulfillment, setFulfillment] = useState("pickup");
  const [address, setAddress] = useState("");
  const [depositPercent, setDepositPercent] = useState(5);
  const [placing, setPlacing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editOptions, setEditOptions] = useState([]);
  const [editSides, setEditSides] = useState([]);

  const openEditSides = async (item) => {
    setEditItem(item);
    setEditSides(item.selected_sides || []);
    setEditOptions([]);
    try {
      setEditOptions(await api.getSideOptions(item.product_id));
    } catch {
      setEditOptions([]);
    }
  };

  const saveEditSides = () => {
    const req = editItem?.sides_config?.required_count || 0;
    if (req > 0 && editSides.length < req) {
      toast.error(`Please choose at least ${req} side${req > 1 ? "s" : ""}`);
      return;
    }
    updateSides(editItem.key, editSides);
    setEditItem(null);
  };

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    api.getPaymentInfo(selectedBusinessId).then((i) => setDepositPercent(i.deposit_percent || 5)).catch(() => {});
    api.listBusinesses().then((all) => {
      const b = (all || []).find((x) => x.id === selectedBusinessId);
      setPaused(!!b?.ordering_paused);
    }).catch(() => {});
  }, [selectedBusinessId]);

  const subtotal = useMemo(() => bizItems.reduce((s, i) => s + priceForItem(i) * i.quantity, 0), [bizItems]);
  const deliveryFee = DELIVERY_OPTIONS.find((d) => d.key === fulfillment)?.fee || 0;
  const total = subtotal + deliveryFee;
  const estDeposit = Math.round(total * (depositPercent / 100) * 100) / 100;
  const estBalance = Math.round((total - estDeposit) * 100) / 100;

  const placeOrder = async () => {
    if (paused) {
      toast.error(`${theme.name} isn't taking orders right now`);
      return;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone");
      return;
    }
    if (!date) {
      toast.error("Please choose a pickup / delivery date");
      return;
    }
    if (fulfillment !== "pickup" && !address.trim()) {
      toast.error("Delivery address is required");
      return;
    }
    let pickup_at = null;
    try {
      pickup_at = new Date(`${date}T${time || "12:00"}`).toISOString();
    } catch (e) {
      /* invalid date — send null */
    }

    setPlacing(true);
    try {
      const order = await api.createOrder({
        business_id: selectedBusinessId,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        pickup_date: date + (time ? ` ${time}` : ""),
        pickup_at,
        notes: notes.trim() || null,
        fulfillment_type: fulfillment,
        delivery_address: fulfillment !== "pickup" ? address.trim() : null,
        items: bizItems.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: i.quantity,
          size: i.size || null,
          flavor: i.flavor || null,
          message_on_cake: i.message_on_cake || null,
          selected_sides: i.selected_sides || [],
          unit_price: priceForItem(i),
          subtotal: Math.round(priceForItem(i) * i.quantity * 100) / 100,
        })),
      });
      // Remove this business's items from cart
      bizItems.forEach((i) => remove(i.key));
      toast.success("Order placed! Complete your deposit to confirm.");
      nav(`/order/${order.id}`);
    } catch (err) {
      toast.error(err.message || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  if (bizItems.length === 0) {
    return (
      <div className="container-app py-20 text-center">
        <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{ background: "var(--surface-2)" }}>
          <ShoppingBag size={28} style={{ color: "var(--muted)" }} />
        </div>
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Your cart is empty</h1>
        <p className="mt-2" style={{ color: "var(--muted)" }}>Add something delicious from {theme.name}.</p>
        <Link to="/shop" className="btn-brand inline-flex mt-6 px-6 py-3 rounded-full font-semibold">
          Browse the menu
        </Link>
      </div>
    );
  }

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  return (
    <div className="container-app py-8 sm:py-12">
      <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>Your cart</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>{theme.name}</p>

      <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        <div className="space-y-4">
          {bizItems.map((i) => (
            <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 rounded-2xl p-3" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
              <AuthImage src={i.image_url} alt={i.product_name} className="h-24 w-24 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <h3 className="font-display text-lg" style={{ color: "var(--ink)" }}>{i.product_name}</h3>
                  <button onClick={() => remove(i.key)} data-testid={`remove-${i.product_id}`} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="text-xs mt-0.5 space-x-2" style={{ color: "var(--muted)" }}>
                  {i.size && <span>{i.size}</span>}
                  {i.flavor && <span>· {i.flavor}</span>}
                </div>
                {i.message_on_cake && <div className="text-xs italic mt-1" style={{ color: "var(--muted)" }}>&ldquo;{i.message_on_cake}&rdquo;</div>}
                {i.sides_config?.enabled && (
                  <div className="mt-1.5" data-testid={`cart-sides-${i.product_id}`}>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                      <span className="font-semibold" style={{ color: "var(--ink)" }}>Sides: </span>
                      {(i.selected_sides || []).length
                        ? i.selected_sides.map((s) => s.name + (s.price ? ` (+${money(s.price)})` : "")).join(", ")
                        : "None selected"}
                    </div>
                    <button onClick={() => openEditSides(i)} data-testid={`edit-sides-${i.product_id}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--brand)" }}>
                      <Pencil size={12} /> Edit sides
                    </button>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center rounded-full" style={{ border: "1px solid var(--border)" }}>
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-2.5 py-1.5" aria-label="Decrease"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm font-semibold">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-2.5 py-1.5" aria-label="Increase"><Plus size={14} /></button>
                  </div>
                  <div className="font-display text-lg" style={{ color: "var(--brand)" }}>
                    {i.is_quote ? "Quote" : money(priceForItem(i) * i.quantity)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => clear(selectedBusinessId)} className="text-sm underline" style={{ color: "var(--muted)" }}>Clear this cart</button>
        </div>

        {/* Checkout details */}
        <div className="rounded-2xl p-6 sticky top-20" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--ink)" }}>Order details</h2>

          <div className="space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="checkout-name" className="w-full rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" data-testid="checkout-phone" className="w-full rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="checkout-date" className="w-full rounded-xl px-3 py-2.5 outline-none" style={inputStyle} />
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="checkout-time" className="w-full rounded-xl px-3 py-2.5 outline-none" style={inputStyle} />
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--ink)" }}>Fulfillment</div>
            <div className="space-y-2">
              {DELIVERY_OPTIONS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => setFulfillment(d.key)}
                  data-testid={`fulfillment-${d.key}`}
                  className="w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-left transition"
                  style={fulfillment === d.key ? { border: "2px solid var(--brand)", background: "var(--accent-soft)" } : { border: "1px solid var(--border)" }}
                >
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--ink)" }}>{d.label}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{d.desc}</div>
                  </div>
                  <div className="text-sm font-semibold" style={{ color: "var(--brand)" }}>{d.fee ? money(d.fee) : "Free"}</div>
                </button>
              ))}
            </div>
            {fulfillment !== "pickup" && (
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address" data-testid="checkout-address" className="w-full mt-2 rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            )}
          </div>

          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the kitchen (optional)" data-testid="checkout-notes" rows={2} className="w-full mt-4 rounded-xl px-4 py-2.5 outline-none resize-none" style={inputStyle} />

          <div className="mt-5 pt-4 space-y-2 text-sm" style={{ borderTop: "1px solid var(--border)" }}>
            <Row label="Subtotal" value={money(subtotal)} />
            <Row label="Delivery" value={deliveryFee ? money(deliveryFee) : "Free"} />
            <div className="flex justify-between font-display text-lg pt-1" style={{ color: "var(--ink)" }}>
              <span>Total</span>
              <span>{money(total)}</span>
            </div>
            <div className="mt-3 rounded-xl p-3" style={{ background: "var(--accent-soft)" }}>
              <div className="flex justify-between text-sm font-semibold" style={{ color: "var(--ink)" }}>
                <span>Due now (deposit ~{depositPercent}%)</span>
                <span data-testid="est-deposit">{money(estDeposit)}</span>
              </div>
              <div className="flex justify-between text-xs mt-1" style={{ color: "var(--muted)" }}>
                <span>Balance later</span>
                <span>{money(estBalance)}</span>
              </div>
            </div>
            {bizItems.some((i) => i.is_quote) && (
              <div className="mt-3 rounded-xl p-3 text-xs" style={{ background: "var(--surface-2)", color: "var(--muted)" }} data-testid="cart-quote-note">
                Your cart includes a custom &ldquo;Etc.&rdquo; item. The baker will review it and contact you with a price — the total and deposit above exclude it until quoted.
              </div>
            )}
          </div>

          {paused && (
            <div className="mt-4 rounded-xl px-4 py-3 text-sm font-semibold" style={{ background: "#fdecec", color: "#b91c1c" }} data-testid="cart-paused-banner">
              {theme.name} isn&apos;t taking orders right now. You can&apos;t check out until they resume.
            </div>
          )}

          <button onClick={placeOrder} disabled={placing || paused} data-testid="place-order-btn" className="btn-brand w-full mt-5 rounded-xl py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {paused ? "Ordering paused" : placing ? "Placing…" : "Place order"} {!placing && !paused && <ArrowRight size={18} />}
          </button>
          <p className="mt-2 text-xs text-center" style={{ color: "var(--muted)" }}>
            Final deposit is confirmed on the next step where you pay.
          </p>
        </div>
      </div>

      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit sides · {editItem?.product_name}</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <SidesPicker
              options={editOptions}
              value={editSides}
              onChange={setEditSides}
              requiredCount={editItem?.sides_config?.required_count || 0}
            />
          </div>
          <button onClick={saveEditSides} data-testid="save-sides-btn" className="btn-brand w-full mt-5 rounded-xl py-3 font-semibold">
            Save sides
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between" style={{ color: "var(--muted)" }}>
      <span>{label}</span>
      <span style={{ color: "var(--ink)" }}>{value}</span>
    </div>
  );
}
